import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { workingRuleTypeLabel } from '../../utils/workingCalendarLabels';
import { summarizeWorkingRule } from '../../utils/workingCalendarRuleSummary';

export type RuleRow = { sortOrder: number; ruleType: string; payload: Record<string, unknown> };

export const WEEKDAY_OPTS = [
  { v: 1, label: 'Mon' },
  { v: 2, label: 'Tue' },
  { v: 3, label: 'Wed' },
  { v: 4, label: 'Thu' },
  { v: 5, label: 'Fri' },
  { v: 6, label: 'Sat' },
  { v: 0, label: 'Sun' }
];

const DOW_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const MONTH_SELECT_OPTS = MONTH_SHORT.map((label, i) => ({ value: i + 1, label }));

export function pad2(n: number) {
  return String(n).padStart(2, '0');
}

export function daysInCalendarMonth(month1to12: number): number {
  const m = Math.min(12, Math.max(1, month1to12));
  return new Date(2024, m, 0).getDate();
}

export function monthGrid(year: number, month: number, firstDayOfWeek: number): (number | null)[] {
  const first = new Date(year, month - 1, 1);
  const jsDow = first.getDay();
  const startBlanks = (jsDow - firstDayOfWeek + 7) % 7;
  const dim = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startBlanks; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function dowHeaderLabels(firstDayOfWeek: number): string[] {
  return Array.from({ length: 7 }, (_, i) => DOW_SHORT[(firstDayOfWeek + i) % 7]!);
}

function annualOffRangeFromPayload(payload: Record<string, unknown>): {
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
} {
  if (payload.startMonth != null && payload.startDay != null) {
    return {
      startMonth: Number(payload.startMonth) || 1,
      startDay: Number(payload.startDay) || 1,
      endMonth: Number(payload.endMonth) || Number(payload.startMonth) || 1,
      endDay: Number(payload.endDay) || Number(payload.startDay) || 1
    };
  }
  const m = Number(payload.month) || 1;
  const d = Number(payload.day) || 1;
  return { startMonth: m, startDay: d, endMonth: m, endDay: d };
}

export function cloneRuleRow(r: RuleRow): RuleRow {
  return {
    sortOrder: r.sortOrder,
    ruleType: r.ruleType,
    payload: JSON.parse(JSON.stringify(r.payload)) as Record<string, unknown>
  };
}

export function previewCellTooltip(
  pv: { working: boolean; expectedHours: number } | undefined
): string {
  if (!pv) return 'No preview data';
  if (pv.working && pv.expectedHours > 0) {
    return `Working · ${pv.expectedHours} h`;
  }
  return `Non-working · ${pv.expectedHours} h`;
}

/** Shared layout for org + member dual-view calendar dialogs. */
export const WORKING_CALENDAR_DUAL_VIEW_DIALOG_PAPER_PROPS = {
  sx: {
    display: 'flex',
    flexDirection: 'column',
    height: { xs: '92dvh', md: 'min(860px, 92dvh)' },
    maxHeight: '92dvh',
    minHeight: { xs: '82vh', md: 'min(720px, 88vh)' }
  }
};

export const WORKING_CALENDAR_DUAL_VIEW_CONTENT_SX = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  py: 2
};

export const WORKING_CALENDAR_DUAL_VIEW_GRID_SX = {
  flex: 1,
  minHeight: { xs: 400, md: 580 },
  alignItems: 'stretch',
  mx: 0,
  width: '100%'
};

export const WORKING_CALENDAR_PREVIEW_PANEL_SX = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: { xs: 380, md: 580 },
  height: '100%'
};

export const WORKING_CALENDAR_PREVIEW_SCROLL_SX = {
  flex: 1,
  minHeight: { xs: 340, md: 520 },
  overflowY: 'auto',
  overflowX: 'hidden',
  pr: 0.5,
  pb: 1,
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(3, 1fr)' },
  gap: 1.5,
  alignContent: 'start'
};

export const WORKING_CALENDAR_RULES_SCROLL_SX = {
  flex: 1,
  minHeight: { xs: 300, md: 420 },
  overflowY: 'auto',
  pr: 1,
  pb: 1
};

export function InfoHintIcon({ title }: { title: string }) {
  return (
    <Tooltip title={title} placement="top" enterDelay={300}>
      <IconButton size="small" aria-label="More information" sx={{ p: 0.25, ml: 0.25 }}>
        <InfoOutlinedIcon fontSize="small" color="action" />
      </IconButton>
    </Tooltip>
  );
}

export function SectionLabelWithHint({ label, hint }: { label: string; hint: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.75 }}>
      <Typography variant="subtitle2">{label}</Typography>
      <InfoHintIcon title={hint} />
    </Box>
  );
}

export function DualViewDialogTitle({
  children,
  hint
}: {
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pr: 2 }}>
      {children}
      {hint ? <InfoHintIcon title={hint} /> : null}
    </DialogTitle>
  );
}

type YearPreviewProps = {
  viewYear: number;
  onViewYearChange: (y: number) => void;
  previewMap: Record<string, { working: boolean; expectedHours: number }>;
  firstDayOfWeek: number;
  timezoneLabel: string;
  /** Shown in the (i) tooltip next to the year — not as inline caption text. */
  previewHelpExtra?: string;
};

export const WorkingCalendarYearPreview: React.FC<YearPreviewProps> = ({
  viewYear,
  onViewYearChange,
  previewMap,
  firstDayOfWeek,
  timezoneLabel,
  previewHelpExtra
}) => {
  const previewHelp = [
    `Times shown in your User preference timezone (${timezoneLabel}).`,
    'Light cells = working day with hours; darker = non-working or 0 h.',
    'Hover a day for expected hours.',
    previewHelpExtra
  ]
    .filter(Boolean)
    .join(' ');

  return (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexShrink: 0 }}>
      <IconButton size="small" onClick={() => onViewYearChange(viewYear - 1)} aria-label="Previous year">
        <ChevronLeftIcon />
      </IconButton>
      <Typography variant="subtitle1" sx={{ flex: 1, textAlign: 'center' }}>
        {viewYear}
      </Typography>
      <InfoHintIcon title={previewHelp} />
      <IconButton size="small" onClick={() => onViewYearChange(viewYear + 1)} aria-label="Next year">
        <ChevronRightIcon />
      </IconButton>
    </Box>
    <Box sx={WORKING_CALENDAR_PREVIEW_SCROLL_SX}>
      {MONTH_SHORT.map((monthName, monthIdx) => {
        const month = monthIdx + 1;
        const cells = monthGrid(viewYear, month, firstDayOfWeek);
        const headers = dowHeaderLabels(firstDayOfWeek);
        return (
          <Box key={month}>
            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
              {monthName}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.25,
                textAlign: 'center'
              }}
            >
              {headers.map((h) => (
                <Typography key={`${h}-${month}`} variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                  {h}
                </Typography>
              ))}
              {cells.map((d, i) => {
                if (d === null) return <Box key={`e-${month}-${i}`} />;
                const key = `${viewYear}-${pad2(month)}-${pad2(d)}`;
                const pv = previewMap[key];
                const bg = !pv
                  ? '#f5f5f5'
                  : pv.working && pv.expectedHours > 0
                    ? '#fafafa'
                    : '#e0e0e0';
                return (
                  <Tooltip key={key} title={previewCellTooltip(pv)} placement="top" enterDelay={300}>
                    <Box
                      sx={{
                        bgcolor: bg,
                        border: '1px solid #ddd',
                        borderRadius: 0.5,
                        py: 0.35,
                        fontSize: 10,
                        cursor: 'default',
                        minHeight: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {d}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  </>
  );
};

type RuleEditDialogProps = {
  open: boolean;
  source: RuleRow | null;
  canManage: boolean;
  onClose: () => void;
  onSave: (row: RuleRow) => void;
};

export const WorkingCalendarRuleEditDialog: React.FC<RuleEditDialogProps> = ({
  open,
  source,
  canManage,
  onClose,
  onSave
}) => {
  const [draft, setDraft] = useState<RuleRow | null>(null);
  const [startDayStr, setStartDayStr] = useState('');
  const [endDayStr, setEndDayStr] = useState('');
  const [hoursStr, setHoursStr] = useState('');
  const [dateHoursStr, setDateHoursStr] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !source) {
      setDraft(null);
      return;
    }
    const c = cloneRuleRow(source);
    setDraft(c);
    setError(null);
    if (c.payload.kind === 'ANNUAL_OFF') {
      const ar = annualOffRangeFromPayload(c.payload);
      setStartDayStr(String(ar.startDay));
      setEndDayStr(String(ar.endDay));
    } else {
      setStartDayStr('');
      setEndDayStr('');
    }
    if (c.payload.kind === 'WEEKDAY_HOURS') {
      setHoursStr(String(Number(c.payload.hours) || 0));
    } else {
      setHoursStr('');
    }
    if (c.payload.kind === 'DATE_HOURS') {
      setDateHoursStr(String(Number(c.payload.hours) || 0));
    } else {
      setDateHoursStr('');
    }
  }, [open, source]);

  const handleSave = () => {
    if (!draft || !canManage) return;
    setError(null);
    const p = draft.payload;

    if (p.kind === 'ANNUAL_OFF') {
      const sm = Number(p.startMonth) || 1;
      const em = Number(p.endMonth) || 1;
      const sdRaw = startDayStr.trim();
      const edRaw = endDayStr.trim();
      if (sdRaw === '' || edRaw === '') {
        setError('Enter start and end day.');
        return;
      }
      const sd = parseInt(sdRaw, 10);
      const ed = parseInt(edRaw, 10);
      if (!Number.isFinite(sd) || !Number.isFinite(ed)) {
        setError('Days must be whole numbers.');
        return;
      }
      const sdClamped = Math.min(Math.max(1, sd), daysInCalendarMonth(sm));
      const edClamped = Math.min(Math.max(1, ed), daysInCalendarMonth(em));
      onSave({
        ...draft,
        payload: {
          kind: 'ANNUAL_OFF',
          startMonth: sm,
          startDay: sdClamped,
          endMonth: em,
          endDay: edClamped
        }
      });
      onClose();
      return;
    }

    if (p.kind === 'WEEKDAY_HOURS') {
      const h = hoursStr.trim() === '' ? NaN : Number(hoursStr);
      if (!Number.isFinite(h)) {
        setError('Enter a valid hours value.');
        return;
      }
      onSave({
        ...draft,
        payload: {
          kind: 'WEEKDAY_HOURS',
          weekdays: (p.weekdays as number[]) || [],
          hours: Math.min(24, Math.max(0, h))
        }
      });
      onClose();
      return;
    }

    if (p.kind === 'DATE_HOURS') {
      const h = dateHoursStr.trim() === '' ? NaN : Number(dateHoursStr);
      if (!Number.isFinite(h)) {
        setError('Enter a valid hours value.');
        return;
      }
      onSave({
        ...draft,
        payload: {
          kind: 'DATE_HOURS',
          date: (p.date as string) || '',
          hours: Math.min(24, Math.max(0, h))
        }
      });
      onClose();
      return;
    }

    if (p.kind === 'DATE_OFF') {
      onSave({
        ...draft,
        payload: { kind: 'DATE_OFF', date: (p.date as string) || '' }
      });
      onClose();
      return;
    }

    onSave(draft);
    onClose();
  };

  if (!draft) return null;

  const ar = draft.payload.kind === 'ANNUAL_OFF' ? annualOffRangeFromPayload(draft.payload) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit rule</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {workingRuleTypeLabel(draft.ruleType)}
        </Typography>

        {draft.payload.kind === 'WEEKDAY_HOURS' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              multiple
              size="small"
              options={WEEKDAY_OPTS}
              getOptionLabel={(o) => o.label}
              value={WEEKDAY_OPTS.filter((o) => ((draft.payload.weekdays as number[]) || []).includes(o.v))}
              onChange={(_, v) =>
                setDraft({
                  ...draft,
                  payload: {
                    ...draft.payload,
                    kind: 'WEEKDAY_HOURS',
                    weekdays: v.map((x) => x.v),
                    hours: Number(draft.payload.hours) || 0
                  }
                })
              }
              renderInput={(params) => <TextField {...params} label="Weekdays" />}
              disabled={!canManage}
            />
            <TextField
              label="Hours"
              size="small"
              value={hoursStr}
              onChange={(e) => setHoursStr(e.target.value)}
              disabled={!canManage}
            />
          </Box>
        )}

        {draft.payload.kind === 'ANNUAL_OFF' && ar && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }} disabled={!canManage}>
              <InputLabel id="arm-sm">From month</InputLabel>
              <Select
                labelId="arm-sm"
                label="From month"
                value={ar.startMonth}
                onChange={(e: SelectChangeEvent<number>) => {
                  const startMonth = Number(e.target.value);
                  setDraft({
                    ...draft,
                    payload: {
                      kind: 'ANNUAL_OFF',
                      startMonth,
                      startDay: ar.startDay,
                      endMonth: ar.endMonth,
                      endDay: ar.endDay
                    }
                  });
                }}
              >
                {MONTH_SELECT_OPTS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="From day"
              size="small"
              value={startDayStr}
              onChange={(e) => setStartDayStr(e.target.value.replace(/\D/g, ''))}
              disabled={!canManage}
              sx={{ width: 100 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }} disabled={!canManage}>
              <InputLabel id="arem-sm">To month</InputLabel>
              <Select
                labelId="arem-sm"
                label="To month"
                value={ar.endMonth}
                onChange={(e: SelectChangeEvent<number>) => {
                  const endMonth = Number(e.target.value);
                  setDraft({
                    ...draft,
                    payload: {
                      kind: 'ANNUAL_OFF',
                      startMonth: ar.startMonth,
                      startDay: ar.startDay,
                      endMonth,
                      endDay: ar.endDay
                    }
                  });
                }}
              >
                {MONTH_SELECT_OPTS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="To day"
              size="small"
              value={endDayStr}
              onChange={(e) => setEndDayStr(e.target.value.replace(/\D/g, ''))}
              disabled={!canManage}
              sx={{ width: 100 }}
            />
          </Box>
        )}

        {(draft.payload.kind === 'DATE_OFF' || draft.payload.kind === 'DATE_HOURS') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Date (YYYY-MM-DD)"
              size="small"
              value={(draft.payload.date as string) || ''}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  payload: { ...draft.payload, date: e.target.value }
                })
              }
              disabled={!canManage}
            />
            {draft.payload.kind === 'DATE_HOURS' && (
              <TextField
                label="Hours"
                size="small"
                value={dateHoursStr}
                onChange={(e) => setDateHoursStr(e.target.value)}
                disabled={!canManage}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {canManage && (
          <Button variant="contained" onClick={handleSave}>
            Save rule
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export function FrozenRulesList({
  rules,
  title,
  hint = 'Read-only — set by your organization.'
}: {
  rules: RuleRow[];
  title: string;
  hint?: string;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <SectionLabelWithHint label={title} hint={hint} />
      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No organization rules on the assigned calendar.
        </Typography>
      ) : (
        rules.map((r, i) => (
          <Paper
            key={i}
            variant="outlined"
            sx={{
              py: 0.75,
              px: 1,
              mb: 0.75,
              bgcolor: 'grey.100',
              borderColor: 'grey.300',
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2" color="inherit">
              <Box component="span" fontWeight={600}>
                {i + 1}.
              </Box>{' '}
              {workingRuleTypeLabel(r.ruleType)}: {summarizeWorkingRule(r)}
            </Typography>
          </Paper>
        ))
      )}
    </Box>
  );
}
